import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LockIcon, LayoutDashboardIcon, UsersIcon, SendIcon, MessageSquareIcon, BriefcaseIcon, UserPlusIcon, ActivityIcon, RefreshCwIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { OverviewPanel, type AdminTab } from '../../components/admin/OverviewPanel';
import { RegistrationsPanel } from '../../components/admin/RegistrationsPanel';
import { OutreachPanel } from '../../components/admin/OutreachPanel';
import { MessagesPanel } from '../../components/admin/MessagesPanel';
import { JobsPanel } from '../../components/admin/JobsPanel';
import { AssignmentsPanel } from '../../components/admin/AssignmentsPanel';
import { ActivityPanel } from '../../components/admin/ActivityPanel';
import { useRegistry } from '../../contexts/RegistryContext';
import { applyPageMeta } from '../../utils/seo';
const OWNER_PASSCODE = 'velnoris-owner';
const SESSION_KEY = 'velnoris.admin.session';

/* ------------------------------- Passcode -------------------------------- */

function PasscodeGate({
  onUnlock
}: {
  onUnlock: () => void;
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  return <main className="flex min-h-screen w-full items-center justify-center bg-ink px-4" data-id="element-564" data-mp="2xaapowihqj2e">
      <form className="w-full max-w-sm border-t-2 border-gold bg-white p-8" onSubmit={e => {
      e.preventDefault();
      if (value === OWNER_PASSCODE) {
        window.sessionStorage.setItem(SESSION_KEY, 'open');
        onUnlock();
        return;
      }
      setError('That passcode is not recognised.');
    }} data-id="element-565" data-mp="20ka7jgcmn0u2">
        <LockIcon aria-hidden="true" className="h-5 w-5 text-gold-700" data-id="element-566" data-mp="2edrpiwpo05hz" />
        <h1 className="mt-4 font-serif text-[1.5rem] leading-tight text-ink" data-id="element-567" data-mp="1gvrmnaynm9l6">Owner console</h1>
        <p className="mt-2 font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-568" data-mp="1lu2u5f1h8808">
          Private to Velnoris Executive Network. Enter your passcode to continue.
        </p>

        <label htmlFor="owner-passcode" className="mt-6 block font-sans text-[0.8125rem] font-medium text-ink" data-id="element-569" data-mp="2k65gduiqbp3f">
          Passcode
        </label>
        <input id="owner-passcode" type="password" autoFocus value={value} onChange={e => {
        setValue(e.target.value);
        setError('');
      }} aria-invalid={Boolean(error)} aria-describedby={error ? 'owner-passcode-error' : undefined} className="mt-2 w-full border border-navy-200 bg-white px-3.5 py-2.5 font-sans text-[0.9375rem] text-ink focus:border-navy-500" data-id="element-570" data-mp="2y0przqkh5aay" />
        {error ? <p id="owner-passcode-error" className="mt-2 font-sans text-[0.8125rem] text-state-negative" data-id="element-571" data-mp="2abdjkswz169p">
            {error}
          </p> : null}

        <div className="mt-6" data-id="element-572" data-mp="3ln60iuckkfl4">
          <Button type="submit" size="lg" data-id="element-573" data-mp="3lqa9s64w61in">
            Unlock console
          </Button>
        </div>
      </form>
    </main>;
}

/* -------------------------------- Console -------------------------------- */

const sections: {
  id: AdminTab;
  label: string;
  Icon: typeof UsersIcon;
}[] = [{
  id: 'overview',
  label: 'Dashboard',
  Icon: LayoutDashboardIcon
}, {
  id: 'registrations',
  label: 'Candidates',
  Icon: UsersIcon
}, {
  id: 'outreach',
  label: 'Outreach',
  Icon: SendIcon
}, {
  id: 'messages',
  label: 'Inbox',
  Icon: MessageSquareIcon
}, {
  id: 'jobs',
  label: 'Jobs',
  Icon: BriefcaseIcon
}, {
  id: 'assignments',
  label: 'Invitations',
  Icon: UserPlusIcon
}, {
  id: 'activity',
  label: 'Activity',
  Icon: ActivityIcon
}];
const intros: Record<AdminTab, string> = {
  overview: 'What needs you today, and everything within one click.',
  registrations: 'Everyone who has registered, with their full submission.',
  outreach: 'Write to a candidate — an invitation, a mandate, or a direct note.',
  messages: 'Conversations started from the public contact page.',
  jobs: 'Write a brief once, then invite executives to apply to it.',
  assignments: 'Who has been invited to which job, and where each stands.',
  activity: 'A record of everything that has happened on the site.'
};
export function AdminConsole() {
  const [unlocked, setUnlocked] = useState(() => window.sessionStorage.getItem(SESSION_KEY) === 'open');
  const [tab, setTab] = useState<AdminTab>('overview');
  const [refreshing, setRefreshing] = useState(false);
  const {
    registrations,
    threads,
    jobs,
    assignments,
    activity,
    refresh,
    syncFromInbox
  } = useRegistry();
  const counts: Record<AdminTab, number | undefined> = {
    overview: undefined,
    registrations: registrations.length,
    outreach: threads.filter(t => t.initiatedBy === 'owner').length,
    messages: threads.filter(t => t.initiatedBy !== 'owner' && t.unreadForOwner).length || undefined,
    jobs: jobs.length,
    assignments: assignments.length,
    activity: activity.length
  };
  useEffect(() => {
    applyPageMeta({
      title: 'Owner console | Velnoris Executive Network',
      description: 'Private owner console.',
      canonicalPath: '/admin',
      noindex: true
    });
  }, []);
  const runSync = useCallback(async () => {
    setRefreshing(true);
    refresh();
    await syncFromInbox();
    setRefreshing(false);
  }, [refresh, syncFromInbox]);

  /* Check for arrivals when the console opens, and periodically after. */
  useEffect(() => {
    if (!unlocked) return;
    void runSync();
    const id = window.setInterval(() => void syncFromInbox(), 30_000);
    return () => window.clearInterval(id);
  }, [runSync, syncFromInbox, unlocked]);
  if (!unlocked) return <PasscodeGate onUnlock={() => setUnlocked(true)} data-id="element-574" data-mp="1ih9v2hsvd74g" />;
  const current = sections.find(s => s.id === tab) ?? sections[0];
  return <div className="min-h-screen w-full bg-parchment lg:flex" data-id="element-575" data-mp="1nheh0ncjqu22">
      {/* Navigation */}
      <div className="on-dark bg-ink lg:w-64 lg:shrink-0" data-id="element-576" data-mp="1ri84xgpzo8uy">
        <div className="flex items-center justify-between gap-4 px-5 py-5 lg:block" data-id="element-577" data-mp="3oht2jvxzqa69">
          <div data-id="element-578" data-mp="3jiybk6pvdfb2">
            <p className="font-serif text-[1.125rem] text-parchment" data-id="element-579" data-mp="3jjhtvojfm5pm">Velnoris</p>
            <p className="mt-0.5 font-sans text-[0.6875rem] uppercase tracking-[0.14em] text-gold" data-id="element-580" data-mp="14ysd81wqto3">Owner console</p>
          </div>
          <Button variant="onDark" size="sm" className="lg:hidden" onClick={() => {
          window.sessionStorage.removeItem(SESSION_KEY);
          setUnlocked(false);
        }} data-id="element-581" data-mp="3sjead2vo3y9l">
            Lock
          </Button>
        </div>

        <nav aria-label="Console sections" className="px-2 pb-4 lg:px-3" data-id="element-582" data-mp="k44be3n2r961">
          <ul className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible" data-id="element-583" data-mp="g2ium77f6aqk">
            {sections.map(({
            id,
            label,
            Icon
          }) => <li key={id} className="shrink-0 lg:w-full" data-id="element-584" data-mp="2lvzdbrjj3zb2">
                <button type="button" onClick={() => setTab(id)} aria-current={tab === id ? 'page' : undefined} className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left font-sans text-[0.875rem] transition-colors duration-120 ${tab === id ? 'bg-navy-700 text-parchment' : 'text-navy-100 hover:bg-navy-700/50 hover:text-parchment'}`} data-id="element-585" data-mp="2htvziw9e0imm">
                  <Icon aria-hidden="true" className="h-4 w-4 shrink-0" data-id="element-586" data-mp="22wmexf0fvt6q" />
                  <span className="flex-1 whitespace-nowrap" data-id="element-587" data-mp="1o056avjmwsnv">{label}</span>
                  {counts[id] ? <span className="font-sans text-[0.6875rem] text-navy-200" data-id="element-588" data-mp="1syopwpar9sd2">{counts[id]}</span> : null}
                </button>
              </li>)}
          </ul>
        </nav>

        <div className="hidden border-t border-navy-700 px-5 py-4 lg:block" data-id="element-589" data-mp="1k1xy4p38kzoa">
          <Link to="/" className="inline-flex items-center gap-1.5 font-sans text-[0.8125rem] text-navy-100 underline underline-offset-2 hover:text-parchment" data-id="element-590" data-mp="39fkpbt13keqv">
            <ExternalLinkIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-591" data-mp="qsnr9cbo080d" />
            View public site
          </Link>
          <div className="mt-4" data-id="element-592" data-mp="lw5lqa8ig5mz">
            <Button variant="onDark" size="sm" onClick={() => {
            window.sessionStorage.removeItem(SESSION_KEY);
            setUnlocked(false);
          }} data-id="element-593" data-mp="h2ucy3k51esf">
              <LockIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-594" data-mp="848z2lpm2nxz" />
              Lock console
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="min-w-0 flex-1 px-5 py-8 md:px-10" data-id="element-595" data-mp="3u94b6epboy1n">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-596" data-mp="3q8ajrbco9140">
          <div data-id="element-597" data-mp="3qa3qeqnablc0">
            <h1 className="font-serif text-[1.875rem] leading-tight text-ink" data-id="element-598" data-mp="3qanaacwhwjso">{current.label}</h1>
            <p className="mt-1.5 max-w-2xl font-sans text-[0.875rem] leading-relaxed text-charcoal-500" data-id="element-599" data-mp="1sg6mmv2kwrmi">
              {intros[tab]}
            </p>
          </div>
          <Button size="sm" variant="secondary" disabled={refreshing} onClick={() => void runSync()} data-id="element-600" data-mp="36drf6tz3ss7o">
            <RefreshCwIcon aria-hidden="true" className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} data-id="element-601" data-mp="3bc2o92hkqyou" />
            {refreshing ? 'Checking…' : 'Refresh'}
          </Button>
        </div>

        <div className="mt-2" data-id="element-602" data-mp="3k4nuxk7xlexb">
          {tab === 'overview' ? <OverviewPanel onNavigate={setTab} data-id="element-603" data-mp="2nfyb3ocquc8p" /> : null}
          {tab === 'registrations' ? <RegistrationsPanel data-id="element-604" data-mp="2ni1a2xvvjfpk" /> : null}
          {tab === 'outreach' ? <OutreachPanel data-id="element-605" data-mp="2sfkp400ebwik" /> : null}
          {tab === 'messages' ? <MessagesPanel data-id="element-606" data-mp="2wgmocgubc8kj" /> : null}
          {tab === 'jobs' ? <JobsPanel data-id="element-607" data-mp="2wetkf9b342g8" /> : null}
          {tab === 'assignments' ? <AssignmentsPanel data-id="element-608" data-mp="31fzcr60u1np8" /> : null}
          {tab === 'activity' ? <ActivityPanel data-id="element-609" data-mp="31dd5l7huta7j" /> : null}
        </div>

        <p className="mt-12 max-w-3xl border-t border-navy-100 pt-5 font-sans text-[0.75rem] leading-relaxed text-charcoal-500" data-id="element-610" data-mp="bqxbg9d66wuh">
          Records are stored in this browser and the passcode is enforced here rather than on a server. Before real
          candidate data depends on it, this needs a database and a proper owner account.
        </p>
      </main>
    </div>;
}